import { useMediaQuery } from '@uidotdev/usehooks';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// project import
import config from 'config';
import useAuth from 'hooks/useAuth';
import { buildAiChatPath } from 'pages/ai-chat/aiChatNavigation';

// types
import { GuardProps } from 'types/auth';
import { AI_CHAT_MOBILE_QUERY } from 'types/aiChat';
import { FeatureFlag } from 'types/features';
import { useHasPermissions } from 'utils/permissionUtil';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { useReturnToLocation } from 'utils/useNavigateWithReturnState';

// ==============================|| GUEST GUARD ||============================== //

const LOGIN_PATHS = ['/', '/login', '/forgot-password', '/check-mail', '/set-password', '/code-verification'];

const GuestGuard = ({ children }: GuardProps): React.ReactElement | null => {
    const { isLoggedIn } = useAuth();
    const navigate = useNavigate();

    const returnTo = useReturnToLocation('/login');
    const location = useLocation();

    // Tenants on the mobile-default flag land in Crespect AI instead of the dashboard when they
    // log in on a phone — only where the assistant is theirs to use at all. It is the landing path
    // alone: a link or bookmark naming another page is still honoured below.
    const isMobile = useMediaQuery(AI_CHAT_MOBILE_QUERY);
    const isMobileAiEnabled = useIsFeatureEnabled(
        [FeatureFlag.AI_CHAT_MOBILE_DEFAULT, FeatureFlag.AI_CHAT_ENABLED],
        true
    );
    const hasAiPermission = useHasPermissions(['READ.CRESPECT_AI']);
    const landingPath = isMobile && isMobileAiEnabled && hasAiPermission ? buildAiChatPath() : config.defaultPath;

    // TODO(react-doctor: no-effect-event-handler): auth-guard navigation triggered
    // by `isLoggedIn` derived from async auth state — framework pattern.
    useEffect(() => {
        if (isLoggedIn) {
            // Enrolment finishes the sign-in itself and then shows its confirmation, so the page stays
            // up after the session exists; its own button leads into the portal.
            if ('/mfa-setup' === location.pathname) {
                return;
            }

            // Check sessionStorage for SSO redirect (React Router state is lost during external navigation)
            let finalReturnTo = returnTo;
            const ssoReturnTo = sessionStorage.getItem('sso_return_to');
            if (ssoReturnTo) {
                finalReturnTo = ssoReturnTo;
                sessionStorage.removeItem('sso_return_to');
            }

            const redirectPath =
                typeof finalReturnTo === 'string' && LOGIN_PATHS.includes(finalReturnTo) ? landingPath : finalReturnTo;
            navigate(redirectPath, { replace: true });
        }
    }, [isLoggedIn, navigate, location.pathname, returnTo, landingPath]);

    return children;
};

export default GuestGuard;
